/**
 * Blocked Overlay Component
 * 
 * Full-screen overlay that appears when Focus Mode is active.
 * Shows countdown timer and top 3 priority tasks from Agenda.
 */

import React, { useEffect, useState } from 'react';
import { useFocusControl } from '../../hooks/useFocusControl';
import { formatRemainingTime } from '../../utils/scrollFreeze';
import { 
  getTopPriorityTasks, 
  formatPriority, 
  formatDeadline 
} from '../../services/agendaTaskService';
import { AgendaTask } from '../../types/agenda.types';
import { ThinkIcon, GoalIcon, CalendarIcon, UnlimitedIcon, ICON_STYLES } from '../icons';

// Filled dot for priority/status (Lucide Circle has no weight prop)
const FilledDot: React.FC<{ color: string; size?: number }> = ({ color, size = 12 }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: color,
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
);

interface BlockedOverlayProps {
  userId: string;
}

// Helper function to get priority icon with soft sage-aligned colors
const getPriorityIcon = (priority?: string) => {
  switch (priority) {
    case 'urgent':
      return <FilledDot color="#C9A5A0" />; // Soft terracotta
    case 'high':
      return <FilledDot color="#D4B896" />; // Soft warm beige
    case 'medium':
      return <FilledDot color="#DDD9BD" />; // Soft yellow-grey
    case 'low':
      return <FilledDot color="#A8BDB5" />; // Light sage
    default:
      return <FilledDot color="#C8C4BD" />; // Soft grey
  }
};

export const BlockedOverlay: React.FC<BlockedOverlayProps> = ({ userId }) => {
  const { state, stopFocus } = useFocusControl();
  const [priorityTasks, setPriorityTasks] = useState<AgendaTask[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch priority tasks when blocked
  useEffect(() => {
    if (state.isBlocked && userId) {
      setLoading(true);
      getTopPriorityTasks(userId)
        .then(tasks => {
          setPriorityTasks(tasks);
          setLoading(false);
        })
        .catch(err => {
          console.error('❌ Error loading priority tasks:', err);
          setLoading(false);
        });
    }
  }, [state.isBlocked, userId]);

  if (!state.isBlocked) return null;

  // Determine block duration text
  const getBlockDurationText = () => {
    if (state.activeMode === 'timer') {
      const hours = Math.floor(state.timer.durationMinutes / 60);
      const minutes = state.timer.durationMinutes % 60;
      if (hours > 0 && minutes > 0) {
        return `${hours} uur en ${minutes} minuten`;
      } else if (hours > 0) {
        return `${hours} uur`;
      } else {
        return `${minutes} ${minutes === 1 ? 'minuut' : 'minuten'}`;
      }
    } else if (state.activeMode === 'block') {
      return `${state.block.durationHours} ${state.block.durationHours === 1 ? 'uur' : 'uren'}`;
    } else if (state.activeMode === 'window') {
      return `tussen ${state.window.startTime} - ${state.window.endTime}`;
    }
    return 'onbepaalde tijd';
  };

  return (
    <div className="fixed inset-0 bg-white/95 backdrop-blur-sm z-[1100] flex items-start justify-center overflow-y-auto px-3 pt-20 pb-24 md:items-center md:p-4">
      <div className="max-w-md md:max-w-2xl w-full mx-auto space-y-3 md:space-y-6 py-3 md:py-8">
        {/* Header */}
        <div className="text-center space-y-2 md:space-y-3">
          <div className="flex justify-center">
            <ThinkIcon size={40} weight="duotone" style={{ color: '#8DA8A0' }} />
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            Focus Modus Actief
          </h2>
          
          <p className="text-sm md:text-lg text-gray-600">
            Je newsfeed is geblokkeerd voor <strong>{getBlockDurationText()}</strong>
          </p>
        </div>

        {/* Countdown Timer */}
        {state.remainingSeconds > 0 && (
          <div className="text-center rounded-lg p-3 md:p-6" style={{ 
            background: 'linear-gradient(to right, rgba(141,168,160,0.08), rgba(165,189,181,0.06))'
          }}>
            <div className="text-4xl md:text-6xl font-mono font-bold mb-1 md:mb-2" style={{ color: '#8DA8A0' }}>
              {formatRemainingTime(state.remainingSeconds)}
            </div>
            <p className="text-xs md:text-sm text-gray-600">resterend</p>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-gray-300" />

        {/* Priority Tasks Section */}
        <div className="space-y-3 md:space-y-4">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 flex items-center gap-2">
            <GoalIcon {...ICON_STYLES.default} size={20} />
            <span>Zie de volgende acties met prioriteit:</span>
          </h3>

          {loading ? (
            <div className="text-center py-6 md:py-12">
              <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-b-2 border-purple-600 mx-auto mb-3"></div>
              <p className="text-sm text-gray-500">Acties laden...</p>
            </div>
          ) : priorityTasks.length === 0 ? (
            <div className="rounded-lg p-4 md:p-8 text-center border" style={{ 
              background: 'linear-gradient(to right, rgba(141,168,160,0.06), rgba(165,189,181,0.04))',
              borderColor: 'rgba(141,168,160,0.15)'
            }}>
              <div className="flex justify-center mb-2 md:mb-3">
                <UnlimitedIcon size={34} weight="duotone" style={{ color: '#8DA8A0' }} />
              </div>
              <p className="text-base md:text-lg font-medium text-gray-900 mb-1">
                Geen openstaande acties gevonden
              </p>
              <p className="text-xs md:text-sm text-gray-600">
                Geweldig werk! Je bent helemaal bij.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {priorityTasks.map((task, index) => (
                <div
                  key={task.id}
                  className="bg-white rounded-lg p-4 hover:shadow-md transition-all duration-200"
                  style={{ border: '2px solid #ECEAE5' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(141,168,160,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#ECEAE5';
                  }}
                >
                  <div className="flex items-start gap-4">
                    {/* Priority number badge */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-lg shadow-md" style={{
                    background: 'linear-gradient(135deg, #8DA8A0 0%, #7A9488 100%)'
                  }}>
                      {index + 1}
                    </div>
                    
                    {/* Task content */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 mb-2 text-lg">
                        {task.title}
                      </h4>
                      
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium px-2 py-1 bg-gray-100 rounded flex items-center gap-1">
                          {getPriorityIcon(task.priority)}
                          <span>{formatPriority(task.priority).replace(/^🔴 |^🟠 |^🟡 |^🟢 |^⚪ /, '')}</span>
                        </span>
                        
                        {task.deadline && (
                          <span className="px-2 py-1 rounded flex items-center gap-1" style={{ 
                            color: '#3C4F49',
                            backgroundColor: 'rgba(212,184,150,0.15)' 
                          }}>
                            <CalendarIcon size={14} weight="regular" />
                            <span>{formatDeadline(task.deadline)}</span>
                          </span>
                        )}
                        
                        {task.status === 'in_progress' && (
                          <span className="font-medium px-2 py-1 rounded flex items-center gap-1" style={{ 
                            color: '#8DA8A0',
                            backgroundColor: 'rgba(141,168,160,0.1)' 
                          }}>
                            <FilledDot color="#8DA8A0" />
                            <span>In uitvoering</span>
                          </span>
                        )}
                      </div>
                      
                      {task.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-300" />

        {/* Action buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={() => window.location.hash = '#agenda'}
            className="px-4 py-2.5 md:px-6 md:py-4 text-white rounded-lg transition-all duration-200 font-semibold text-base md:text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
            style={{ 
              background: 'linear-gradient(135deg, #8DA8A0 0%, #7A9488 100%)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #7A9488 0%, #6A8478 100%)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #8DA8A0 0%, #7A9488 100%)';
            }}
          >
            <CalendarIcon size={18} weight="bold" />
            <span>Ga naar Agenda</span>
          </button>
          
          <button
            onClick={stopFocus}
            className="px-4 py-2.5 md:px-6 md:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm md:text-base"
          >
            Stop Focus Modus
          </button>
        </div>

        {/* Motivational footer */}
        <div className="hidden md:block text-center">
          <p className="text-sm text-gray-500 italic">
            "Focus is de sleutel tot meesterschap"
          </p>
        </div>
      </div>
    </div>
  );
};
